import { useEffect, useState } from 'react';
import type { ConnectionState } from '../lib/ws';

/** Only a provider event may name compaction; a quiet socket is just quiet. */
export function ComposerStatus({
  streaming,
  startedAt,
  compacting = false,
  connectionState,
  lastActivityAt,
  statusMessage,
}: {
  streaming: boolean;
  startedAt: number | null;
  compacting?: boolean;
  connectionState: ConnectionState;
  lastActivityAt: number | null;
  statusMessage: string | null;
}) {
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    if (!streaming) return;
    const tick = () => setNow(Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [streaming]);

  if (!streaming) return null;

  const elapsed = startedAt === null ? null : Math.max(0, Math.floor((now - startedAt) / 1000));
  const quietFor =
    lastActivityAt === null ? null : Math.max(0, Math.floor((now - lastActivityAt) / 1000));
  const quiet = quietFor !== null && quietFor >= 60;
  const reconnecting = connectionState === 'closed' || connectionState === 'connecting';
  const label = reconnecting
    ? 'Переподключаемся к чату…'
    : compacting
      ? quiet
        ? `Сжимаю контекст · нет обновлений ${formatElapsed(quietFor)}`
        : 'Сжимаю контекст…'
      : quiet
        ? `Нет обновлений ${formatElapsed(quietFor)} · ждём ответ`
        : statusMessage?.trim() ||
          (startedAt === null ? 'Запрос отправлен, ждём ответа…' : 'Модель работает…');

  return (
    <output
      className="mb-2 flex min-w-0 items-center gap-1.5 text-[12px] text-muted-foreground"
      aria-live="polite"
      aria-atomic="true"
    >
      <span
        className="inline-block h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-primary"
        aria-hidden="true"
      />
      <span className="min-w-0 truncate">
        {label}
        {elapsed !== null && <span aria-hidden="true"> · {formatElapsed(elapsed)}</span>}
      </span>
    </output>
  );
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}с`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}м ${s}с`;
}
